// monitoring-ui/hooks/useMonitoring.ts

import { useState, useEffect, useRef, useCallback } from 'react';
import { MonitoringService } from '../Service/MonitoringService';
import { useUser } from '../Core/UserContext';
import axiosInstance from '../Core/RestHelper';
import type {
  DeploymentSummary,
  CostSummary,
  DeploymentCostRow,
  AlertEvent,
  RightsizingRec,
  WSMessage,
  WSMetricsUpdate,
  WSAlertMessage,
} from '../Model/Monitoring';

type Tab = 'overview' | 'deployments' | 'costs' | 'ai' | 'alerts';

interface PlanPerks {
  monitor_dashboard: string | null;
  [key: string]: any;
}

interface Plan {
  plan_name: string;
  price: number;
  perks: PlanPerks;
  plan_id: number;
  [key: string]: any;
}

interface MonitoringState {
  deployments:      DeploymentSummary[];
  costSummary:      CostSummary | null;
  deploymentCosts:  DeploymentCostRow[];
  rightsizing:      RightsizingRec[];
  activeAlerts:     WSAlertMessage[];
  isLoading:        boolean;
  error:            string | null;
  lastUpdated:      Date | null;
  wsConnected:      boolean;
  currentPlan:      Plan | null;
  hasMonitorAccess: boolean;
}

const MAX_BACKOFF_MS  = 30_000;
const BASE_BACKOFF_MS = 2_000;

export const useMonitoring = (deploymentId?: string) => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const user = useUser();

  const [state, setState] = useState<MonitoringState>({
    deployments:      [],
    costSummary:      null,
    deploymentCosts:  [],
    rightsizing:      [],
    activeAlerts:     [],
    isLoading:        true,
    error:            null,
    lastUpdated:      null,
    wsConnected:      false,
    currentPlan:      null,
    hasMonitorAccess: false,
  });

  const wsRef         = useRef<WebSocket | null>(null);
  const retryCountRef = useRef<number>(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectWSRef  = useRef<() => void>(() => {});

  const applyMetricsUpdate = useCallback((msg: WSMetricsUpdate) => {
    setState(prev => ({
      ...prev,
      lastUpdated: new Date(),
      deployments: prev.deployments.map(dep =>
        dep.deployment_id === msg.deployment_id
          ? {
              ...dep,
              cpu_pct:            msg.ec2.cpu_pct,
              mem_pct:            msg.ec2.mem_pct,
              instance_state:     msg.ec2.instance_state,
              ecs_running_count:  msg.ecs?.running_count ?? dep.ecs_running_count,
              ecs_desired_count:  msg.ecs?.desired_count ?? dep.ecs_desired_count,
              estimated_cost_mtd: msg.cost?.estimated_cost_mtd ?? dep.estimated_cost_mtd,
              last_updated:       msg.collected_at,
            }
          : dep,
      ),
    }));
  }, []);

  const applyAlert = useCallback((msg: WSAlertMessage) => {
    setState(prev => {
      const existing = prev.activeAlerts.find(
        a => a.deployment_id === msg.deployment_id && a.metric_name === msg.metric_name,
      );
      if (existing) {
        return {
          ...prev,
          activeAlerts: prev.activeAlerts.map(a =>
            a.deployment_id === msg.deployment_id && a.metric_name === msg.metric_name ? msg : a,
          ),
        };
      }
      return {
        ...prev,
        activeAlerts: [msg, ...prev.activeAlerts],
        deployments: prev.deployments.map(dep =>
          dep.deployment_id === msg.deployment_id
            ? { ...dep, active_alert_count: dep.active_alert_count + 1 }
            : dep,
        ),
      };
    });
  }, []);

  const connectWS = useCallback(() => {
    if (!user?.id) return;

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    wsRef.current?.close();

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl    = `${protocol}//${process.env.REACT_APP_WS_HOST}/ws/metrics/${user.id}`;
    const ws       = new WebSocket(wsUrl);

    ws.onopen = () => {
      retryCountRef.current = 0;
      setState(prev => ({ ...prev, wsConnected: true }));
    };

    ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data);
        if (msg.type === 'metrics_update') applyMetricsUpdate(msg);
        if (msg.type === 'alert')          applyAlert(msg);
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      setState(prev => ({ ...prev, wsConnected: false }));
      if (!user?.id) return;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** retryCountRef.current, MAX_BACKOFF_MS);
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(() => connectWSRef.current(), delay);
    };

    ws.onerror = () => ws.close();
    wsRef.current = ws;
  }, [applyMetricsUpdate, applyAlert, user?.id]);

  useEffect(() => {
    connectWSRef.current = connectWS;
  }, [connectWS]);

  const loadAll = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const [deployments, costSummary, deploymentCosts, rightsizing, dashRes] = await Promise.all([
        MonitoringService.getDeployments(),
        MonitoringService.getCostSummary(),
        MonitoringService.getDeploymentCosts(),
        MonitoringService.getRightsizing(),
        axiosInstance.get('/users/dashboard'),
      ]);

      const plan: Plan | null = dashRes.data?.plan_details ?? null;
      const hasMonitorAccess  = !!plan?.perks?.monitor_dashboard;

      setState(prev => ({
        ...prev,
        deployments,
        costSummary,
        deploymentCosts,
        rightsizing,
        currentPlan:      plan,
        hasMonitorAccess,
        isLoading:        false,
        lastUpdated:      new Date(),
      }));
    } catch (e: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: e?.response?.data?.detail ?? 'Failed to load monitoring data',
      }));
    }
  }, []);

  const resolveAlert = useCallback(async (alertId: number, deploymentId: string, metricName: string) => {
    await MonitoringService.resolveAlert(alertId);
    setState(prev => ({
      ...prev,
      activeAlerts: prev.activeAlerts.filter(
        a => !(a.deployment_id === deploymentId && a.metric_name === metricName),
      ),
      deployments: prev.deployments.map(dep =>
        dep.deployment_id === deploymentId
          ? { ...dep, active_alert_count: Math.max(0, dep.active_alert_count - 1) }
          : dep,
      ),
    }));
  }, []);

  useEffect(() => {
    loadAll();
    return () => {
      wsRef.current?.close();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    connectWS();
  }, [connectWS]);

  return {
    ...state,
    activeTab,
    setActiveTab,
    loadAll,
    resolveAlert,
  };
};